/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useCallback, useEffect, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {TemperatureType} from '@ion/core/src/types/general';
import {isImperial} from '@ion/core/src/units/helpers';
import {Unit} from '@ion/core/src/units/units';
import {celsiusToFahrenheit, fahrenheitToCelsius} from '@ion/core/src/utils/Conversions';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {LabelPlacement} from '@ion/dls/src/components/switch/legacy/Switch';
import Switch from '@ion/dls/src/components/switch/Switch';
import RadioButton from '../components/radio/RadioButton';
import {SCIDeviceSettingsFormField, TemperatureControlProps} from './DeviceEditorUtils';
import {handleSCISettingsValidation, SCIDeviceSettingsValidatorProps} from './helpers';

export type DeviceThresholdsChooserProps = {
  readOnly?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>, temperatureProps?: TemperatureControlProps) => void;
  classes?: GenericObject;
  defaultValue?: string;
  name?: string;
  fieldLabel?: string;
  checked?: boolean;
  firstInputProps?: any;
  secondInputProps?: any;
  setIsValid?: Function;
  units?: string;
  systemOfUnits?: string;
  onToggleThreshold?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void
};

const styleOverrides = () => ({
  root : {
    '& input' : {
      height : '35px'
    },
    width : '70%'
  },
  inputRoot : {
    '& input' : {
      height      : '35px',
      paddingLeft : '11px'
    }
  }
});

/**
 * getTemperatureUnits returns Celsius of Fahrenheit in reference to
 * User profile
 */
const getTemperatureUnits = (type: string, systemOfUnits: string): string => {
  if (type === SCIDeviceSettingsFormField.TemperatureAlert) {
    return isImperial(systemOfUnits as Unit) ? TemperatureType.Fahrenheit : TemperatureType.Celsius;
  };
  return null;
};

/**
 * @DeviceThresholdsChooser
 *
 * The DeviceThresholdsChooser provides various input fields based on
 * types of thresholds for SCI devices.
 *
 * ```typescript
 *
 *     import {DeviceThresholdsChooser} from '@ion/dls/src/editors/';
 *
 *    <DeviceThresholdsChooser
 *      fieldLabel={'fieldLabel'}
 *      name={'name'}
 *      checked={isChecked}
 *      setIsValid={setIsValid}
 *      onToggleThreshold={handleSwitchChange}
 *      units={'G'}
 *      firstInputProps={
 *        fieldName : 'MinTemp',
 *        value     : formState[SCIDeviceSettingsFormField.MinTemp]?.value,
 *        errorMsg  : 'error!'
 *      }
 *      secondInputProps={
 *        fieldName : 'MaxTemp',
 *        value     : formState[SCIDeviceSettingsFormField.MaxTemp]?.value,
 *        errorMsg  : 'error!'
 *      }
 *      onChange={handleFieldChange}
 *      readOnly={loading || isViewMode}
 *      systemOfUnits={systemOfUnits}
 *       />*

 *
 * ```
 *
 * @param {Function} setIsValid: Callback prop will be invoked with the input value validation.
 *   If valid, it will change the state to true.
 * @param {Function} onChange: Callback prop which will be invoked with input field changes
 * @param {Function} onToggleThreshold: Callback prop which will be invoked with the switch toggles
 * @param {object} firstInputProps: an object contains information for the primary input field
 *   used in the component -- including fieldName, value, and errorMsg.
 * @param {object} secondInputProps: an object contains information for the secondary input field
 *   used in the component (if any) -- including fieldName, value, and errorMsg.
 * @param {string} fieldLabel: Overrides the default field label
 * @param {string} units: if provided, units will be shown next to the 'Minimum' or 'Maximum'
 * @param {string} name: for the component that gets passed to the root element.
 * @param {boolean} readOnly: When true, the component will be displayed in read-only mode;
 *   that is, disabled. Defaults to false.
 * @param {boolean} checked: When true, the component will be editable, and the switch component will
 *   be shown as 'On'
 */
export const DeviceThresholdsChooser = ({
  readOnly,
  onChange: _onChange,
  name: fieldName,
  fieldLabel,
  classes,
  checked,
  setIsValid,
  units,
  onToggleThreshold,
  firstInputProps,
  secondInputProps,
  systemOfUnits
}: DeviceThresholdsChooserProps) => {
  const [temperatureType, setTemperatureType] = useState(getTemperatureUnits(fieldName, systemOfUnits));
  const [firstValue, setFirstValue]           = useState(firstInputProps?.value);
  const [secondValue, setSecondValue]         = useState(secondInputProps?.value);
  const [error, setError]                     = useState(false);

  // convert values based on temperature type
  useEffect(() => {
    if (temperatureType === TemperatureType.Fahrenheit) {
      firstValue && setFirstValue(Math.round(celsiusToFahrenheit(firstValue, 3)));
      secondValue && setSecondValue(Math.round(celsiusToFahrenheit(secondValue, 3)));
    };
  }, []);

  const displayErrorMsg = useCallback(() => {
    if (fieldName === SCIDeviceSettingsFormField.TemperatureAlert) {
      return temperatureType === TemperatureType.Celsius
        ? firstInputProps?.errorMsg.Metric : firstInputProps?.errorMsg.Imperial;
    };
    return firstInputProps?.errorMsg;
  }, [temperatureType]);

  const onChange = useCallback((event): void => {
    event.preventDefault();
    const {name, value} = event.target;

    const validationProps: SCIDeviceSettingsValidatorProps = {
      name,
      value : parseInt(value),
      temperatureType,
      firstValue,
      secondValue
    };

    const isValid = handleSCISettingsValidation(validationProps);

    setIsValid(isValid);
    setError(!isValid);

    if (name === firstInputProps?.fieldName) {
      setFirstValue(value);
    } else {
      setSecondValue(value);
    };
    const temperatureProps: TemperatureControlProps = {
      isNumber     : true,
      isFahrenheit : fieldName === SCIDeviceSettingsFormField.TemperatureAlert
        ? (temperatureType === TemperatureType.Fahrenheit) : null
    };
    isValid && _onChange(event, temperatureProps);
  }, [temperatureType, firstInputProps]);

  const onChangeRadioButton = useCallback((): void => {
    if (temperatureType === TemperatureType.Fahrenheit) {
      setTemperatureType(TemperatureType.Celsius);
      setFirstValue(fahrenheitToCelsius(parseFloat(firstValue), 3));
      setSecondValue(fahrenheitToCelsius(parseFloat(secondValue), 3));
    } else {
      setTemperatureType(TemperatureType.Fahrenheit);
      setFirstValue(celsiusToFahrenheit(parseFloat(firstValue), 3));
      setSecondValue(celsiusToFahrenheit(parseFloat(secondValue), 3));
    };
  }, [temperatureType, firstValue, secondValue]);

  return (<>
    <Switch name={fieldName}
      disabled={readOnly}
      label={fieldLabel}
      labelPlacement={LabelPlacement.START}
      checked={checked}
      onChange={onToggleThreshold}
      marginLeft={'0px'}/>

    <div className={classes.threshHolderSubContainer}>
      {firstInputProps && (
        <div className={classes.threshHolderFieldContainer}>
          <FormTextField fieldLabel={secondInputProps
            ? `${translate('admin-devices:editors.deviceThresholdsChooser.min')} (${units})`
            : `${translate('admin-devices:editors.deviceThresholdsChooser.max')} (${units})`}
          name={firstInputProps?.fieldName}
          onChange={onChange}
          changed={true}
          value={firstValue}
          styleOverrides={styleOverrides}
          error={error}
          disabled={!checked || readOnly}/>
        </div>
      )}
      {secondInputProps && (
        <div className={classes.threshHolderFieldContainer}>
          <FormTextField fieldLabel={`${translate('admin-devices:editors.deviceThresholdsChooser.max')} (${units})`}
            name={secondInputProps?.fieldName}
            onChange={onChange}
            changed={true}
            value={secondValue}
            error={error}
            styleOverrides={styleOverrides}
            disabled={!checked || readOnly}/>
        </div>
      )}
      {
        fieldName === SCIDeviceSettingsFormField.TemperatureAlert && (
          <>
            <RadioButton label={'°C'}
              checked={temperatureType === TemperatureType.Celsius}
              onChange={onChangeRadioButton}
              disabled={!checked || readOnly || error}/>
            <RadioButton label={'°F'}
              checked={temperatureType === TemperatureType.Fahrenheit}
              onChange={onChangeRadioButton}
              disabled={!checked || readOnly || error}/>
          </>
        )}
    </div>
    <div className={classes.threshHolderErrorContainer}>
      {error? displayErrorMsg() : null}&nbsp;
    </div>
  </>);
};
export default DeviceThresholdsChooser;
